import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link as LinkIcon, Undo2, Redo2, Eraser } from "lucide-react";

/**
 * Compact TipTap rich editor for long-form paragraphs in the CMS.
 * Toolbar: bold, italic, h2, h3, ul, ol, link, undo/redo, clear.
 */
export default function RichEditor({ value, onChange, placeholder = "Write here…", testid = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[180px] focus:outline-none px-4 py-3 text-foreground/90 leading-relaxed",
        "data-testid": `${testid}-content`,
      },
    },
  });

  // Sync external value changes (e.g. on locale switch, on doc reload)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const btn = (active, onClick, key, Icon, title, testidSuffix) => (
    <button
      key={key}
      type="button"
      title={title}
      onClick={onClick}
      data-testid={`${testid}-${testidSuffix}`}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-primary/15 text-primary"
          : "text-foreground/70 hover:bg-muted hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
    </button>
  );

  const onLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    // StarterKit doesn't include Link by default in v3; gracefully ignore if absent
    if (editor.commands.toggleLink) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/40 px-2 py-1.5">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "b", Bold, "Bold", "bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "i", Italic, "Italic", "italic")}
        <span className="w-px h-4 bg-border mx-1" />
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "h2", Heading2, "Heading 2", "h2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "h3", Heading3, "Heading 3", "h3")}
        <span className="w-px h-4 bg-border mx-1" />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "ul", List, "Bulleted list", "ul")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "ol", ListOrdered, "Numbered list", "ol")}
        <span className="w-px h-4 bg-border mx-1" />
        {btn(false, onLink, "lk", LinkIcon, "Link (optional)", "link")}
        <span className="flex-1" />
        {btn(false, () => editor.chain().focus().undo().run(), "un", Undo2, "Undo", "undo")}
        {btn(false, () => editor.chain().focus().redo().run(), "re", Redo2, "Redo", "redo")}
        {btn(false, () => editor.chain().focus().clearNodes().unsetAllMarks().run(), "cl", Eraser, "Clear formatting", "clear")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
